import { DESCENDING } from 'constants/sortDirections';
import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { BossBadge } from 'ui/badge';
import { DestructiveButton, PrimaryButton, SecondaryDestructiveButton } from 'ui/button';
import { Add16 } from 'ui/icons/action/Add16';
import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { Table } from 'ui/table';
import { sortRows } from 'ui/table/utils';
import { TableHeader } from 'ui/tableHeader';
import type { ColumnData } from 'ui/tableHeader/constants';
import { TableRows } from 'ui/tableRows';
import { body2, heading3, spacing } from 'ui/theme';
import { LeftTooltip } from 'ui/tooltip';
import { INTEGRATIONS_OPTIONS } from '../constants';
import { useIntegrationMetadataContext } from '../context/integrationMetadata';
import { ModalType, useModalContext } from '../context/modal';
import { Integration } from '../types';
import { ExpirationBadge } from './AppDetails/IntegrationTable/components';

const SectionHeader = styled.div`
  ${heading3}
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.base};
`;

const StyledTable = styled(Table)`
  margin-top: ${spacing.base};
`;

const Cell = styled.div`
  ${body2};
  box-sizing: border-box;
  display: table-cell;
  overflow-y: visible;
  padding: ${spacing.threeQuarter};
  margin: 0;
`;

const ActionsWrapper = styled.div`
  display: flex;
  gap: ${spacing.half};
`;

type RowProps = {
  row: Integration;
};

const configureColumns = (
  handleAssign: (integration: Integration) => void,
  handleDelete: (integration: Integration) => void,
  buttonIsDisabled: boolean,
  isBaus?: boolean,
) => [
  {
    name: 'Integration name',
    value: 'name',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => <Cell>{row.name}</Cell>,
  } as ColumnData,
  {
    name: 'Platform',
    value: 'platform',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => <Cell>{INTEGRATIONS_OPTIONS[row.platform].name}</Cell>,
  } as ColumnData,
  {
    name: 'Valid starting',
    value: 'notValidBefore',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => (
      <Cell>{row.notValidBefore ? <ExpirationBadge dateTime={row.notValidBefore} /> : 'N/A'}</Cell>
    ),
  } as ColumnData,
  {
    name: 'Expires',
    value: 'notValidAfter',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => (
      <Cell>{row.notValidAfter ? <ExpirationBadge dateTime={row.notValidAfter} /> : 'N/A'}</Cell>
    ),
  } as ColumnData,
  {
    name: 'OS notification',
    value: 'androidUseOsNotifications',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => {
      switch (row.androidUseOsNotifications) {
        case undefined:
          return <Cell>N/A</Cell>;
        case false:
          return <Cell>No</Cell>;
        default:
          return <Cell>Yes</Cell>;
      }
    },
  } as ColumnData,
  {
    name: 'Devices',
    value: 'deviceCount',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => <Cell>{row.deviceCount}</Cell>,
  } as ColumnData,
  {
    name: 'Actions',
    value: '',
    align: 'flex-start',
    RowCell: ({ row }: RowProps) => (
      <Cell>
        <ActionsWrapper>
          <PrimaryButton
            data-test="assign-app-button"
            isDisabled={buttonIsDisabled}
            onClick={() => handleAssign(row)}
          >
            Assign app
          </PrimaryButton>
          {isBaus && (
            <DestructiveButton
              data-test="unassigned-integration-delete-button"
              onClick={() => handleDelete(row)}
            >
              <Delete16 />
            </DestructiveButton>
          )}
        </ActionsWrapper>
      </Cell>
    ),
  } as ColumnData,
];

type Props = {
  integrations: Integration[];
  showHeader?: boolean;
};
export const UnassignedIntegrations = ({ integrations, showHeader = true }: Props) => {
  const isBaus = useSelector(selectIsBossOrSalesBossLevel);

  const { mobileApps, setSelectedIntegration } = useIntegrationMetadataContext();
  const { openModal } = useModalContext();

  const [sortedBy, setSortedBy] = useState<keyof Integration>('name');
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const sortedRows = sortRows(integrations, sortedBy, sortDirection);

  const openAssignModal = (selected: Integration) => {
    setSelectedIntegration(selected);
    openModal(ModalType.AssignIntegrationModal);
  };

  const openCreateModal = () => {
    openModal(ModalType.ConfigureUnassignedIntegrationModal);
  };

  const openDeleteModal = (selected: Integration) => {
    setSelectedIntegration(selected);
    openModal(ModalType.DeleteIntegrationModal);
  };

  const assignAppsButtonDisabled = mobileApps.length === 0;
  const tableColumns = configureColumns(
    openAssignModal,
    openDeleteModal,
    assignAppsButtonDisabled,
    isBaus,
  );

  /**
   * This table is hard to test locally. It is specifically designed for showing legacy integrations that were created
   * in a time before we had mobile apps. It only appears if one of these legacy integrations exists in a project, and
   * there is no way to create new ones via the app.
   *
   * So, to test it, the best way is to add some mock data as the integrations. Below is a sample of the mock data, which 
   * was taken from the redux store from the Mobile SDK TEST engineering project in the engineering org, found at: state.mobileApps.integrations.unassigned:
   *
   * unassigned: [
        {
          name: 'scDemoAppDev',
          notValidBefore: 1531341478000,
          projectId: 1226,
          deviceCount: 0,
          platform: 'APNS',
          notValidAfter: 1565469478000,
          arn: 'arn:aws:sns:us-east-1:203113165317:app/APNS/prod-project-1226-scDemoAppDev'
        },
        {
          name: 'iterable_prod_test',
          projectId: 1226,
          deviceCount: 0,
          platform: 'GCM',
          androidUseOsNotifications: false,
          arn: 'arn:aws:sns:us-east-1:203113165317:app/GCM/prod-project-1226-iterable_prod_test'
        },
        {
          name: 'iossdk_apns_dev',
          notValidBefore: 1472588759000,
          projectId: 1226,
          deviceCount: 0,
          platform: 'APNS_SANDBOX',
          notValidAfter: 1504124759000,
          arn: 'arn:aws:sns:us-east-1:203113165317:app/APNS_SANDBOX/prod-project-1226-iossdk_apns_dev'
        },
      ]

   * replace the sortedRows in the table below with the mock data above to test table locally.
   */

  return !sortedRows.length ? null : (
    <div data-test="mobileApps-unnasignedIntegrations">
      {showHeader && (
        <SectionHeader data-test="unassigned-integrations-header">
          Unassigned Integrations
          {isBaus && (
            <LeftTooltip
              isUsedOnButton
              tooltipContent="🚨 Convenience feature intended only to provide an easy way for on-call engineers to create unassigned integrations if needed to help with troubleshooting issues that customers are experiencing with their existing unassigned integrations 🚨"
            >
              <SecondaryDestructiveButton
                data-test="unassigned-integration-create-button"
                onClick={openCreateModal}
              >
                <Add16 />
                Create <BossBadge />
              </SecondaryDestructiveButton>
            </LeftTooltip>
          )}
        </SectionHeader>
      )}
      <StyledTable>
        <TableHeader
          columns={tableColumns}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
        />
        <TableRows idKey="name" columns={tableColumns} rows={sortedRows} />
      </StyledTable>
    </div>
  );
};
